import { AntDesign } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import React, { useState, useEffect } from "react";
import ContentLoader, { Rect } from "react-content-loader/native";
import { useTranslation } from "react-i18next";
import {
  View,
  ActivityIndicator,
  Text,
  Pressable,
  useWindowDimensions,
} from "react-native";
import { DatePickerModal } from "react-native-paper-dates";
import { useDispatch } from "react-redux";

import TitleXl from "@/components/TitleXl";
import PickDateModal from "@/components/modal/banking/PickDateModal";
import TransactionModal from "@/components/modal/banking/TransactionModal";
import AccountSection from "@/components/sections/AccountsSection";
import TransactionsSection from "@/components/sections/TransactionsSection";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { toastInstance } from "@/utils/ToastConfig";
import { getTransactions } from "@/utils/transactions/GetTransactions";

export default function Home() {
  const [showTransaction, setShowTransaction] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const [showPickModal, setShowPickModal] = useState(false);
  const [isRangeSelection, setIsRangeSelection] = useState(false);
  const [isFilteredSelection, setIsFilteredSelection] = useState(false);
  const [isFetchingTransactions, setIsFetchingTransactions] = useState(false);
  const [currentFilter, setCurrentFilter] = useState("");
  const { height, width } = useWindowDimensions();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const date = new Date();
  const globalValues = useAppSelector((state: any) => state);

  useFocusEffect(
    React.useCallback(() => {
      dispatch(setForm({ currentMenuItem: t("home/home:option.home") }));
      dispatch(setForm({ isTransactionsFiltered: false }));
    }, [])
  );

  useEffect(() => {
    if (globalValues.transactionModalVisible) {
      setShowTransaction(true);
    }
  }, [globalValues.transactionModalVisible]);

  const handleSelection = async (
    value: string,
    range?: { startDate: ""; endDate: "" }
  ) => {
    const PAGE = 0;
    const PAGE_SIZE = 1000;
    if (value === currentFilter) {
      dispatch(setForm({ isTransactionsFiltered: false }));
      dispatch(setForm({ transactionsHasChanges: true }));
      setCurrentFilter("");
      setIsRangeSelection(false);
      setIsFilteredSelection(false);
      setIsFetchingTransactions(false);
      return;
    } else if (value === "lastfifteen") {
      setIsFetchingTransactions(true);
      const currentDate = new Date();

      const fifteenDaysAgo = new Date(
        currentDate.getTime() - 15 * 24 * 60 * 60 * 1000
      );

      const request = await getTransactions(
        globalValues.currentAccountId,
        PAGE,
        PAGE_SIZE,
        fifteenDaysAgo,
        currentDate
      );

      if (request.error) {
        toastInstance("error", t("home/home:errors.filter"));
        setIsFetchingTransactions(false);
        return;
      }

      if (request) {
        dispatch(setForm({ transactionsData: request.transactions }));
        dispatch(setForm({ isTransactionsFiltered: true }));
        setIsFetchingTransactions(false);
        setIsFilteredSelection(true);
      }
    } else if (value === "lastthirty") {
      setIsFetchingTransactions(true);
      const currentDate = new Date();
      const fifteenDaysAgo = new Date(
        currentDate.getTime() - 30 * 24 * 60 * 60 * 1000
      );

      const request = await getTransactions(
        globalValues.currentAccountId,
        PAGE,
        PAGE_SIZE,
        fifteenDaysAgo,
        currentDate
      );

      if (request.error) {
        toastInstance("error", t("home/home:errors.filter"));
        setIsFetchingTransactions(false);
        return;
      }

      if (request) {
        dispatch(setForm({ transactionsData: request.transactions }));
        dispatch(setForm({ isTransactionsFiltered: true }));
        setIsFetchingTransactions(false);
        setIsFilteredSelection(true);
      }
    } else if (value === "custom") {
      setIsFetchingTransactions(true);

      const { startDate, endDate } = range;

      const startDateObject = new Date(startDate);
      const endDateObject = new Date(endDate);

      const request = await getTransactions(
        globalValues.currentAccountId,
        PAGE,
        PAGE_SIZE,
        startDateObject,
        endDateObject
      );

      if (request.error) {
        toastInstance("error", t("home/home:errors.filter"));
        setIsFetchingTransactions(false);
        return;
      }

      if (request) {
        dispatch(setForm({ transactionsData: request.transactions }));
        dispatch(setForm({ isTransactionsFiltered: true }));
        setIsFetchingTransactions(false);
        setIsRangeSelection(true);
        setIsFilteredSelection(true);
      }
    } else if (currentFilter === "") {
      return;
    }
    setCurrentFilter(value);
  };

  const handleDate = (date) => {
    if (!date) {
      setShowDate(false);
      return;
    }
    handleSelection("custom", date);
    setShowDate(false);
  };

  return (
    <View
      className={`${
        globalValues.accountsIsLoading ? "p-0" : "p-4"
      } grow bg-white1  lg:p-0`}
    >
      {globalValues.accountsIsLoading && (
        <View className="absolute bg-white1 z-50 h-full w-full">
          <ContentLoader
            viewBox={`0 0 ${width} ${height}`}
            backgroundColor="#F9F9F9"
            foregroundColor="#E4E4E4"
          >
            <Rect x="0" y="0" rx="5" ry="5" width={width} height={50} />
            <Rect x="0" y="55" rx="5" ry="5" width={width} height={220} />
            <Rect x="0" y="320" rx="5" ry="5" width={width} height={50} />
            <Rect x="0" y="375" rx="5" ry="5" width={width} height="100%" />
          </ContentLoader>
        </View>
      )}
      <View className="flex-col w-full h-auto mb-2">
        <View className="flex-row w-full items-center justify-center gap-2 mb-2">
          <TitleXl text={t("home/home:sections.accounts")} />
        </View>
        <AccountSection
          data={globalValues.accountsData}
          clearFilter={() => handleSelection(currentFilter)}
        />
      </View>
      <View className="w-full items-center mt-4">
        <TitleXl text={t("home/home:sections.transactions")} />
      </View>
      <View className="flex-grow px-1 py-3 relative">
        <View className="bg-white1 rounded-lg p-8 h-full w-full items-center lg:p-6 shadow">
          <View className="absolute top-1 m-1 flex-row items-center lg:m-4">
            {isFilteredSelection && (
              <Pressable
                onPress={() => {
                  handleSelection(currentFilter);
                }}
                className="flex-row items-end"
              >
                <AntDesign name="close" size={12} color="#BDBDBD" />
                <Text className="text-xs text-midgray mx-1">
                  {t("common:placeholder.clearfilter")}
                </Text>
              </Pressable>
            )}
            <Pressable // onPress={() => handleSelection("lastfifteen")}
              onPress={() =>
                toastInstance(
                  "info",
                  "Filter functionality temporarily disabled"
                )
              }
            >
              <Text
                className={`text-xs text-midgray hover:text-darkergray mx-2 ${
                  currentFilter === "lastfifteen"
                    ? "text-purple1"
                    : "text-midgray"
                }`}
              >
                {t("common:placeholder.lastfifteen")}
              </Text>
            </Pressable>
            <Pressable
              // onPress={() => handleSelection("lastthirty")}
              onPress={() =>
                toastInstance(
                  "info",
                  "Filter functionality temporarily disabled"
                )
              }
            >
              <Text
                className={`text-xs text-midgray hover:text-darkergray mx-2 ${
                  currentFilter === "lastthirty"
                    ? "text-purple1"
                    : "text-midgray"
                }`}
              >
                {t("common:placeholder.lastthirty")}
              </Text>
            </Pressable>
            <Pressable
              // onPress={() => setShowDate(true)}
              onPress={() =>
                toastInstance(
                  "info",
                  "Filter functionality temporarily disabled"
                )
              }
              className={`flex-row py-0.5 px-0.5 rounded-full w-20 items-center justify-center mx-1 ${
                isRangeSelection
                  ? "bg-purple1"
                  : "bg-white1 border border-midgray hover:border-darkergray"
              }`}
            >
              <AntDesign
                name="filter"
                size={12}
                color={`${isRangeSelection ? "#FFFFFF" : "#BDBDBD"}`}
              />
              <Text
                className={`text-xs text-midgray mx-1 ${
                  isRangeSelection ? "text-white1" : "text-midgray"
                }`}
              >
                {t("common:placeholder.filter")}
              </Text>
            </Pressable>
            <Pressable
              // onPress={() => setShowPickModal(true)}
              onPress={() =>
                toastInstance(
                  "info",
                  "Export functionality temporarily disabled"
                )
              }
              className="flex-row rounded-full border border-midgray hover:border-darkergray py-0.5 px-0.5 w-20 items-center justify-center mx-1"
            >
              <AntDesign name="export" size={12} color="#BDBDBD" />
              <Text className="text-xs text-midgray ml-1">
                {t("common:placeholder.export")}
              </Text>
            </Pressable>
          </View>
          {globalValues.transactionsIsLoading &&
          !globalValues.moreTransactionsLoading ? (
            <View className="flex-grow items-center justify-center z-50 w-full h-full">
              <ActivityIndicator size="large" color="#5D0CBA" />
            </View>
          ) : isFetchingTransactions ? (
            <View className="flex-grow flex-col gap-2 items-center justify-center z-50 w-full h-full">
              <Text className="text-lg text-darkergray">
                {t("common:terms.fetchingtransactions")}
              </Text>
              <ActivityIndicator size="large" color="#5D0CBA" />
            </View>
          ) : (
            <View className="absolute top-[58px] left-3 right-3 bottom-3">
              <TransactionsSection data={globalValues.transactionsData} />
              {globalValues.moreTransactionsLoading && (
                <View className="flex-row items-center justify-center w-full">
                  <ActivityIndicator size="small" color="#5D0CBA" />
                </View>
              )}
            </View>
          )}
        </View>
      </View>
      <TransactionModal
        visible={showTransaction}
        onClose={() => {
          setShowTransaction(false);
          dispatch(setForm({ transactionModalVisible: false }));
        }}
        transaction={globalValues.transactionResponse}
      />
      <PickDateModal
        visible={showPickModal}
        onClose={() => {
          setShowPickModal(false);
        }}
        accountId={globalValues.currentAccountId}
        accountAssedId={globalValues.currentAccountAssetType}
      />
      <DatePickerModal
        locale="en"
        mode="range"
        visible={showDate}
        startDate={date}
        endDate={date}
        onDismiss={() => setShowDate(false)}
        date={date}
        onConfirm={(params) => handleDate(params)}
        animationType="fade"
        presentationStyle="pageSheet"
        label={t("common:placeholder.selectdaterange")}
        saveLabel={t("common:button.save")}
      />
    </View>
  );
}
